// Búsqueda de comercios por nombre, razón social o identificador tributario (RUT / CUIT).
//
// Es la ÚNICA definición de "este comercio coincide con lo que escribí" para los buscadores
// de cobro (filtro de comercio de la lista y paso "Comercios" del wizard). Antes cada uno
// buscaba distinto —react-select ignoraba tildes y el wizard no— y ninguno encontraba por RUT.
//
// Trabaja sobre DATOS PLANOS ({ name, legalName, businessIdentifier }) y no sobre la opción
// de react-select: su `filterOption` recibe `{ label, value, data }` y los campos extra quedan
// dentro de `data`. Un matcher que leyera `option.legalName` funcionaría en el wizard y en el
// filtro buscaría solo por nombre, sin avisar. Por eso el filtro usa un adaptador:
// `filterOption={(o, q) => matchCommerce(o.data, q)}`.

import { cleanRut } from './rut';

/**
 * Mínimo de caracteres de identificador para buscar por RUT. Con 1 o 2 dígitos la consulta
 * está contenida en el RUT de casi toda la cartera y la lista deja de filtrar.
 */
export const MIN_IDENTIFIER_QUERY_LENGTH = 3;

/** Minúsculas, sin tildes y con los espacios colapsados. */
export function normalizeSearchText(value) {
  return String(value ?? '')
    .normalize('NFD')
    // Rango de diacríticos combinantes escrito explícito (no \p{M}): CRA compila este
    // archivo para navegadores viejos. Mismo criterio que utils/communes.js.
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

// ¿La consulta tiene forma de RUT o CUIT escrito a mano? Solo dígitos, puntos, guiones y
// espacios, con una K final opcional (dígito verificador chileno).
//
// Sin esta guarda, "Tienda 245" se buscaría TAMBIÉN como RUT "245" y traería cualquier
// comercio cuyo RUT contenga esos dígitos. Y `cleanRut` de un nombre conserva sus K
// ("kiosko" → "KK"), así que un nombre nunca debe pasar por la rama del identificador.
const looksLikeIdentifier = (query) => /\d/.test(query) && /^[\d.\-\s]+k?$/.test(query);

/**
 * Clave de búsqueda precalculada de un comercio. Conviene guardarla en la opción al mapear
 * (`searchKey`): el filtro corre en cada tecla contra toda la cartera.
 *
 * @param {{ name?: string|null, legalName?: string|null, businessIdentifier?: string|null }} commerce
 * @returns {{ texts: string[], identifier: string }}
 */
export function buildCommerceSearchKey({ name, legalName, businessIdentifier } = {}) {
  return {
    texts: [name, legalName].map(normalizeSearchText).filter(Boolean),
    identifier: cleanRut(businessIdentifier),
  };
}

/**
 * ¿El comercio coincide con la consulta? Nombre O razón social (sin tildes ni mayúsculas) O
 * RUT limpio de al menos {@link MIN_IDENTIFIER_QUERY_LENGTH} caracteres contenido en el RUT
 * limpio del comercio. Consulta vacía = coincide.
 *
 * @param {{ name?: string|null, legalName?: string|null, businessIdentifier?: string|null,
 *           searchKey?: { texts: string[], identifier: string } }} data datos planos
 * @param {string} query texto tal como lo escribió el usuario
 * @returns {boolean}
 */
export function matchCommerce(data, query) {
  const q = normalizeSearchText(query);
  if (!q) return true;
  const key = data?.searchKey ?? buildCommerceSearchKey(data ?? {});
  if (key.texts.some((text) => text.includes(q))) return true;
  if (!looksLikeIdentifier(q)) return false;
  const identifierQuery = cleanRut(q);
  return (
    identifierQuery.length >= MIN_IDENTIFIER_QUERY_LENGTH &&
    key.identifier.includes(identifierQuery)
  );
}

/**
 * Segunda línea de un comercio en un listado: "razón social · RUT". La razón social se omite
 * si repite el nombre (sería ruido). Cadena vacía si no hay nada que agregar.
 */
export function commerceSecondaryLine({ name, legalName, businessIdentifier } = {}) {
  const parts = [];
  const legal = String(legalName ?? '').trim();
  if (legal && normalizeSearchText(legal) !== normalizeSearchText(name)) parts.push(legal);
  const identifier = String(businessIdentifier ?? '').trim();
  if (identifier) parts.push(identifier);
  return parts.join(' · ');
}

/**
 * Opción de selector a partir de un comercio de `users/companies/active-contracts`
 * (`{ _id, name, legalName, businessIdentifier }`). Conserva los datos planos y la clave de
 * búsqueda para que `matchCommerce(option, q)` funcione tal cual sobre la opción.
 *
 * @param {{ _id: string, name?: string, legalName?: string|null, businessIdentifier?: string|null }} company
 * @param {string} fallbackLabel rótulo si el comercio no tiene nombre
 */
export function toCommerceOption(company, fallbackLabel = '') {
  const name = company?.name || '';
  const legalName = company?.legalName ?? null;
  const businessIdentifier = company?.businessIdentifier ?? null;
  return {
    value: String(company?._id ?? ''),
    label: name || fallbackLabel,
    name,
    legalName,
    businessIdentifier,
    searchKey: buildCommerceSearchKey({ name, legalName, businessIdentifier }),
  };
}
